import Bugsnag from "@bugsnag/js";
import { createBrowserHistory } from "history";
import noop from "lodash/noop";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import HeaderCategories from "@Components/molecules/header/HeaderCategories";
import HeaderMenu from "@Components/molecules/header/HeaderMenu";
import LoginModal from "@Components/molecules/LoginModal";
import WithLocale from "@Components/WithLocale";
import useBannerKountriesHeight from "@Hooks/banner/useBannerKountriesHeight";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import useCartContext from "@Hooks/useCartContext";
import useCheerzApiProvider from "@Hooks/useCheerzApiProvider";
import { ApiErrorResponse } from "@Types/api/error";
import { Article } from "@Types/cart";
import { Category, HeaderMenuLink } from "@Types/header";
import { ScreenAction } from "@Types/login";
import UserContent from "@Types/user";
import { convertHeaderCategoriesFromApiToContent } from "@Utils/header";
import { draftsLink, memoryBoxLink, ordersLink, profileLink, referralsLink } from "@Utils/links";
import { convertUserFromApiToContent } from "@Utils/user";
import "./style.scss";

const history = createBrowserHistory();

export const HEADER_HEIGHT = 118;
export const HEADER_MOBILE_HEIGHT = 56;
export const HEADER_MOBILE_BOTTOM_BAR_HEIGHT = 64;

interface Props {
  cartArticlesLength: number;
  categories: Category[] | undefined;
  draftsNumber: number;
  signOut: () => void;
  user: UserContent | undefined;
}

interface ContainerProps {
  userData?: UserContent;
}

export const Header: FunctionComponent<Props> = ({
  cartArticlesLength,
  categories,
  draftsNumber,
  signOut,
  user,
}) => {
  const [action, setAction] = useState<ScreenAction>("signUp");
  const [displayLoginModal, setDisplayLoginModal] = useState<boolean>(false);
  const intl = useIntl();
  const localeSlug = intl.locale;

  const bannerKountriesHeight = useBannerKountriesHeight();
  const { isLargeScreen } = useBreakpoint();

  const accountLinks: HeaderMenuLink[] = [
    { label: "orders", url: `/${localeSlug}${ordersLink}` },
    {
      label: "drafts",
      url: `/${localeSlug}${draftsLink}`,
    },
    { label: "profile", url: `/${localeSlug}${profileLink}` },
    {
      label: "memoryBox",
      url: `/${localeSlug}${memoryBoxLink}`,
    },
    {
      label: "referrals",
      url: `/${localeSlug}${referralsLink}`,
    },
  ];

  const handleDisplayLoginModal = (action: ScreenAction) => {
    setAction(action);
    setDisplayLoginModal(true);
  };

  if (user && user.isGuest) {
    accountLinks.unshift({
      className: "HeaderAccount-login",
      label: "login",
      onclick: (action?: ScreenAction) => (action ? handleDisplayLoginModal(action) : noop),
    });
  } else {
    accountLinks.unshift({
      className: "HeaderAccount-signOut",
      label: "signOut",
      name: user?.firstName,
      onclick: signOut,
    });
  }

  return (
    <div className="Header" style={{ marginTop: isLargeScreen ? bannerKountriesHeight : 0 }}>
      <HeaderMenu
        accountLinks={accountLinks}
        cartArticlesLength={cartArticlesLength}
        draftsNumber={draftsNumber}
        login={!!user && user.isGuest}
      />
      <HeaderCategories
        accountLinks={accountLinks}
        cartArticlesLength={cartArticlesLength}
        categories={categories}
        draftsNumber={draftsNumber}
      />
      <LoginModal
        action={action}
        display={displayLoginModal}
        hide={() => setDisplayLoginModal(false)}
      />
    </div>
  );
};

const HeaderContainer: FunctionComponent<ContainerProps> = ({ userData }) => {
  const [categories, setCategories] = useState<Category[]>();
  const [draftsNumber, setDraftsNumber] = useState<number>(0);
  const [cartArticlesLength, setCartArticlesLength] = useState<number>(0);
  const [userContent, setUserContent] = useState<UserContent>();
  const { cart, dispatch } = useCartContext();

  const localeSlug = window.location.pathname.split("/")[1];

  const cheerzApiProvider = useCheerzApiProvider();

  const getArticleLength = (articles: Article[]) => {
    return articles.reduce((acc, curr) => acc + curr.quantity, 0);
  };

  const getCallResponsesWithoutUser = () => {
    cheerzApiProvider
      .getDrafts()
      .then((drafts) => {
        setDraftsNumber(drafts.length);
        dispatch &&
          dispatch({
            draftsNumber: drafts.length,
            type: "setDraftsNumber",
          }); // To avoid error with Header not in full React page which has not CartContext
        return cheerzApiProvider.getCart();
      })
      .then((cart) => {
        setCartArticlesLength(cart.articles.length);
      })
      .catch((error: Error) => {
        Bugsnag.notify(error);
      });
  };

  const getCallResponsesWithUser = () => {
    cheerzApiProvider
      .getUser()
      .then((userResponse) => {
        setUserContent(convertUserFromApiToContent(userResponse));
        return getCallResponsesWithoutUser();
      })
      .catch((error: Error) => {
        Bugsnag.notify(error);
      });
  };

  useEffect(() => {
    if (cart) {
      setCartArticlesLength(getArticleLength(cart.articles));
    }
  }, [cart]);

  useEffect(() => {
    cheerzApiProvider
      .getCategories()
      .then((categoriesResponse) => {
        setCategories(convertHeaderCategoriesFromApiToContent(categoriesResponse));
        if (!!userData) {
          return getCallResponsesWithoutUser();
        } else {
          return getCallResponsesWithUser();
        }
      })
      .catch((error: Error) => Bugsnag.notify(error));
  }, [cheerzApiProvider]);

  const handleSignOut = () => {
    cheerzApiProvider
      .signOut()
      .then(() => (window.location.href = `/${localeSlug}`))
      .catch((error: ApiErrorResponse) =>
        Bugsnag.notify(
          Error(
            error.response?.data?.error_description[0]
              ? `${error.response?.data?.error_description[0]}`
              : "Error on signOut call"
          )
        )
      );
  };

  return (
    <WithLocale history={history}>
      <Header
        cartArticlesLength={cartArticlesLength}
        categories={categories}
        draftsNumber={draftsNumber}
        signOut={() => handleSignOut()}
        user={userData || userContent}
      />
    </WithLocale>
  );
};

export default HeaderContainer;
